import { useState, useCallback, useMemo } from 'react';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import useLabelingStore from '../store/useDetectionStore.js';
import useModelManagementStore from '../../../modelManagement/store/useModelManagementStore';
import Swal from "sweetalert2";
import { handleGetOneAPI, handleDownloadDetectionDataAPI, startDetectionTrainingAPI, saveDetectionTrainingConfigAPI } from '../api/detectionLabelApi.js'
import { api_labelflow } from '../../../../../config.jsx';
import { resolveMediaUrl } from '../../../../../utils/mediaUrl';

const DEFAULT_COLORS = [
  '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
  '#FF9F40', '#8BC34A', '#E91E63', '#9C27B0', '#00BCD4'
];

const useDetectionLabelHook = () => {
  const navigate = useNavigate();
  const { modelInfo, setModelInfo, setModelDataInfo, modelDataInfo } = useModelManagementStore();
  const [lastSelectedImage, setLastSelectedImage] = useState(null);
  // const BATCH_SIZE = 10; // Adjust depending on performance

  const {

    classes, setClasses, color, images, addimages, selectedImage, setSelectedImage, selectedImages, setSelectedImages, dragOver, activeDragClass,
    setDragOver, showDeleteButton, setShowDeleteButton, setActiveDragClass, setImages, setCurrentClassId

  } = useLabelingStore();


  // Handle unstructured image upload with progress
  const handleUnstructuredUpload = useCallback(async (files) => {
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));

    // Set upload status

    const newImages = [];

    for (const file of imageFiles) {
      // Simulate processing time for large files
      //   await new Promise(resolve => setTimeout(resolve, 50));
      
      const newImage = {
        id: Math.random().toString(36).substr(2, 9),
        file,
        name: file.name,
        url: file.size <= 1 * 1024 * 1024 ? URL.createObjectURL(file) : "",
        assignedClass: null,
        originalWidth: file.width,
        originalHeight: file.height,
        scale: 1,
        annotations: [],
        polygons: [],
      };

      newImages.push(newImage);
    }

    addimages(newImages);

    // Auto-select the first image if none is selected
    if (!selectedImage && newImages.length > 0) {
      setSelectedImage(newImages[0]);
    }


  }, []);





  // Handle drag and drop events
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false)
  };

  const handleDrop = (e, targetClass = null) => {
    e.preventDefault();
    setDragOver(false);

    const imageId = e.dataTransfer.getData('imageId');
    if (!imageId) {
      // Handle file drop
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleUnstructuredUpload(files);
      }
      return;
    }

    // Handle image move between sections
    // const image = findImageById(imageId);
    // if (!image) return;


    //   moveImageToUnstructured(image);

  };
  // Move image to unstructured section
  //   const moveImageToUnstructured = (image) => {
  //     // Remove from current class
  //     if (image.assignedClass !== null) {
  //       removeClassImage(image.assignedClass, image.id);
  //     }
  //     // Add to unstructured
  //     const updatedImage = { ...image, assignedClass: null };
  //     addUnstructuredImages([updatedImage]);
  //   };
  const handleFileInput = (e, classId = null) => {

    handleUnstructuredUpload(e.target.files);
  };



  const handleSaveDetectionData = async (options = {}) => {
    const {
      forceUpdateExisting = false,
      silent = false,
      skipNavigate = false,
    } = options;

    const { images } = useLabelingStore.getState();

    if (!Array.isArray(images) || images.length === 0) {
      Swal.fire("No images", "Please upload or load at least one image before saving.", "warning");
      return;
    }

    let isConfirmed = true; // default assume new version
    let isNewVersion = true;

    // ✅ Check if versions exist and > 0
    if (forceUpdateExisting) {
      const existingId = modelDataInfo?._id || modelDataInfo?.id || modelDataInfo?.model_data_info_id;
      if (!existingId) {
        const message = "No existing dataset version found to update. Please save the dataset first.";
        if (!silent) {
          Swal.fire("Unavailable", message, "warning");
        }
        throw new Error(message);
      }
      isNewVersion = false;
    } else if (modelDataInfo?.version || (modelInfo?.versions && modelInfo.versions.length > 0)) {
      const result = await Swal.fire({
        title: "Create New Version?",
        text: "Do you want to create a new dataset version or update the existing one?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "New Version",
        cancelButtonText: "Update Existing",
        reverseButtons: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      if (result.isConfirmed) {
        isNewVersion = true;
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        const existingId = modelDataInfo?._id || modelDataInfo?.id || modelDataInfo?.model_data_info_id;
        if (!existingId) {
          Swal.fire("Unavailable", "No existing dataset version found to update.", "warning");
          return;
        }
        isNewVersion = false;
      } else {
        return;
      }
    } else {
      console.log("No existing versions, proceeding to save as new version.");
    }


    const formData = new FormData();
    formData.append("project_id", modelInfo?.projectId || modelInfo?.project_id);
    formData.append("model_id", modelInfo?._id || modelInfo?.model_id);
    formData.append("model_type", modelInfo?.modelType || modelInfo?.model_type);
    formData.append("owner_id", modelInfo?.ownerId || modelInfo?.owner_id);
    formData.append("classes", JSON.stringify(classes));

    const targetModelDataInfoId = modelDataInfo?._id || modelDataInfo?.id || modelDataInfo?.model_data_info_id || null;
    formData.append("is_new_version", !!isNewVersion);
    formData.append("model_data_info_id", isNewVersion ? null : targetModelDataInfoId);

    // Classes metadata
    formData.append(
      "images",
      JSON.stringify(
        images.map((img) => ({
          id: img.id,
          name: img.fileName,
          annotations: img.annotations,
          polygons: img.polygons,
          url: !!img.file ? null : img?.url,
          thumbnailUrl: !!img.file ? null : img?.thumbnailUrl,
          isNew: !!img.file,
          originalWidth: img?.file?.width || img?.originalWidth,
          originalHeight: img?.file?.height || img?.originalHeight,
        })),

      )
    );



    images.forEach((img) => {
      if (img.file) {
        formData.append("image_files", img.file, img.fileName);
      }
    });


    

    try {
      const response = await axios.post(
        `${api_labelflow}/detection_management/save_detection_data`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const savedId = response?.data?.model_info_id || targetModelDataInfoId || null;
      if (modelDataInfo) {
        modelDataInfo._id = savedId || modelDataInfo?._id;
      }
      if (!silent) {
        Swal.fire("Saved!", "Detection data saved successfully.", "success");
      }
      if (!skipNavigate) {
        navigate("/ModelManagement", { state: { projectId: modelInfo?.projectId || modelInfo?.project_id } });
      }
      return savedId;
    } catch (error) {
      console.error("Error saving detection data", error);
      const message = error?.response?.data?.message || "Failed to save detection data.";
      if (!silent) {
        Swal.fire("Error", message, "error");
      }
      throw new Error(message);
    }
  };

  const handleGetOneDetectionData = useCallback(async (params) => {

    try {
      const response = await handleGetOneAPI(params);
      
      const reportData = response?.data || {};
      
      const model = reportData?.model || {};
      const resModelDataInfo = reportData?.model_info || {};
      const classes = reportData?.model_info?.classes || [];
      const items = reportData?.model_data_items_info || [];


      setModelInfo(model);
      setModelDataInfo(resModelDataInfo);

      // Build classes with images
      // const transformedClasses = classes.map((cls) => {
      //   const classIdStr = String(cls.id);

      //   const images = items
      //     .filter((item) => String(item.classId) === classIdStr)
      //     .map((item) => ({
      //       id: item.imageId,
      //       assignedClass: cls.id,
      //       file: null,
      //       name: item.name,
      //       url: item.url,
      //       thumbnailUrl: item.thumbnailUrl

      //     }));

      //   return {
      //     ...cls,
      //     images,
      //   };
      // });

      // console.log("Transformed Classes:", transformedClasses);
      // console.log("Unstructured Images:", unstructuredImages);
      const classWithColor = classes.map((cls, idx) => {
        const baseColor = cls.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length];
        return {
          ...cls,
          id: String(cls.id),
          color: baseColor,
          originalColor: baseColor,
        };
      });
      setClasses(classWithColor);
      if (classWithColor.length > 0) {
        setCurrentClassId(classWithColor[0].id);
      }

      const imagesPreprocessed = items.map(item => ({
        id: item.imageId,
        fileName: item.name || "Unnamed",
        file: null,
        url: item.url,
        thumbnailUrl: item.thumbnailUrl,
        url_display: resolveMediaUrl(item?.url_display),
        thumbnail_url_display: resolveMediaUrl(item?.thumbnail_url_display),
        annotations: Array.isArray(item.annotations) ? item.annotations.map(ann => {
          const normalizedClassId = String(ann.classId);
          const classData = classWithColor.find(cls => cls.id === normalizedClassId) || null;
          return {
            ...ann,
            classId: normalizedClassId,
            class: classData,
          };
        }) : [],
        polygons: Array.isArray(item.polygons) ? item.polygons.map(poly => {
          const normalizedClassId = String(poly.classId);
          const classData = classWithColor.find(cls => cls.id === normalizedClassId) || null;
          return {
            ...poly,
            classId: normalizedClassId,
            class: classData,
          };
        }) : [],
        createdOn: item.createdOn,
        createdBy: item.createdBy,
        originalWidth: item.originalWidth,
        originalHeight: item.originalHeight,
        scale: 1,
      }));
      setImages(imagesPreprocessed);
    } catch (error) {
      console.error("Error fetching summary report:", error);
    } finally {
      // setIsLoading(false);
    }
  }, [setClasses, setImages, setModelDataInfo, setModelInfo]);



  const handleDownloadDetectionData = async () => {
    try {
      // Show preparing loader
      Swal.fire({
        title: "Preparing data...",
        text: "Packaging label data for download.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Silently save to existing version (no prompt), do not navigate
      const savedId = await handleSaveDetectionData({
        forceUpdateExisting: true,
        silent: true,
        skipNavigate: true,
      });
      if (!savedId) {
        Swal.close();
        return;
      }
      const model_data_info_id = savedId || modelDataInfo?._id || 'new_dataset';
      // Direct download without format selection (backend default)
      const params = { model_data_info_id };

      // Call API to download ZIP
      const response = await handleDownloadDetectionDataAPI(params);

      // Create a Blob from the response data
      const blob = new Blob([response.data], { type: 'application/zip' });
      
      // Create a temporary URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dataset_${model_data_info_id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      Swal.close();
      return { status: 'success' };
    } catch (error) {
      console.error('Download failed:', error);
      Swal.close();
      Swal.fire("Error", `Download failed: ${error.message}`, "error");
      return { status: 'error', message: error.message };
    }
  };

  const handleStartDetectionTraining = async (trainingConfig = {}) => {
    const { images } = useLabelingStore.getState();
    const currentModelDataInfoId = modelDataInfo?._id || modelDataInfo?.id || modelDataInfo?.model_data_info_id;

    if (!currentModelDataInfoId) {
      Swal.fire("Dataset Required", "Please save the dataset or load an existing version before starting training.", "warning");
      return false;
    }

    if (!Array.isArray(images) || images.length === 0) {
      Swal.fire("No Images", "Add at least one image before starting training.", "warning");
      return false;
    }

    const hasAnnotations = images.some((img) => Array.isArray(img.annotations) && img.annotations.length > 0);
    if (!hasAnnotations) {
      Swal.fire("No Annotations", "Add at least one bounding box annotation for YOLO training.", "warning");
      return false;
    }

    const isPrivate = (modelInfo?.visibility || "private").toLowerCase() === "private";
    const confirmTitle = isPrivate ? "Payment Required" : "Start Training?";
    const confirmText = isPrivate
      ? "Private model training requires a $5 payment. Make your model public (Model Management → Edit) for free training, or proceed to pay $5 and start training."
      : "YOLOv8 detection training will run in the background using the current dataset.";

    try {
      const confirmation = await Swal.fire({
        title: confirmTitle,
        text: confirmText,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: isPrivate ? "Proceed with $5" : "Start",
        cancelButtonText: "Cancel",
      });

      if (!confirmation.isConfirmed) {
        return false;
      }

      Swal.fire({
        title: "Saving dataset and starting training...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      let persistedModelDataInfoId = currentModelDataInfoId;
      try {
        persistedModelDataInfoId = await handleSaveDetectionData({
          forceUpdateExisting: true,
          silent: true,
          skipNavigate: true,
        });
      } catch (saveError) {
        Swal.close();
        Swal.fire("Save Failed", saveError.message || "Failed to save dataset before training.", "error");
        return false;
      }
      if (!persistedModelDataInfoId) {
        Swal.close();
        Swal.fire("Save Failed", "Dataset could not be saved before training.", "error");
        return false;
      }

    const clampPercent = (value, fallback) => {
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return fallback;
      return Math.max(0, Math.min(100, Math.round(numeric)));
    };

    const normalizedConfig = {
      modelVariant: trainingConfig?.modelVariant || "yolov8n.pt",
      epochs: Math.min(Math.max(Number(trainingConfig?.epochs) || 5, 1), 100),
      imgsz: Math.max(Number(trainingConfig?.imgsz) || 640, 32),
      batchSize: Math.max(1, Number(trainingConfig?.batchSize) || 16),
      stratify: !!trainingConfig?.stratify,
      trainSplit: clampPercent(trainingConfig?.trainSplit, 70),
      valSplit: clampPercent(trainingConfig?.valSplit, 20),
      testSplit: clampPercent(trainingConfig?.testSplit, 10),
      enableEarlyStopping: !!trainingConfig?.enableEarlyStopping,
      patience: trainingConfig?.enableEarlyStopping ? Math.max(1, Math.min(200, Number(trainingConfig?.patience) || 50)) : null,
    };

    const splitSum = normalizedConfig.trainSplit + normalizedConfig.valSplit + normalizedConfig.testSplit;
    if (splitSum !== 100 && splitSum > 0) {
      normalizedConfig.trainSplit = Math.round((normalizedConfig.trainSplit / splitSum) * 100);
      normalizedConfig.valSplit = Math.round((normalizedConfig.valSplit / splitSum) * 100);
      normalizedConfig.testSplit = Math.max(0, 100 - (normalizedConfig.trainSplit + normalizedConfig.valSplit));
    }

    if (normalizedConfig.trainSplit < 50) {
      normalizedConfig.trainSplit = 50;
      const remaining = 50;
      const valRatio = normalizedConfig.valSplit / (normalizedConfig.valSplit + normalizedConfig.testSplit || 1);
      normalizedConfig.valSplit = Math.round(remaining * valRatio);
      normalizedConfig.testSplit = Math.max(0, 100 - (normalizedConfig.trainSplit + normalizedConfig.valSplit));
    }

    const payload = {
      model_data_info_id: persistedModelDataInfoId,
      epochs: normalizedConfig.epochs,
      imgsz: normalizedConfig.imgsz,
      model_name: normalizedConfig.modelVariant,
      batch_size: normalizedConfig.batchSize,
      stratify: normalizedConfig.stratify ? 1 : 0,
      train_split: normalizedConfig.trainSplit,
      val_split: normalizedConfig.valSplit,
      test_split: normalizedConfig.testSplit,
    };
    
    if (normalizedConfig.enableEarlyStopping && normalizedConfig.patience) {
      payload.patience = normalizedConfig.patience;
    }

      const requestedBy = modelInfo?.ownerId || modelInfo?.owner_id;
      if (requestedBy) {
        payload.requested_by = requestedBy;
      }

      try {
        const configPayload = {
          model_data_info_id: persistedModelDataInfoId,
          model_type: "detection",
          model_name: normalizedConfig.modelVariant,
          epochs: normalizedConfig.epochs,
          imgsz: normalizedConfig.imgsz,
          batch_size: normalizedConfig.batchSize,
          stratify: normalizedConfig.stratify ? 1 : 0,
          train_split: normalizedConfig.trainSplit,
          val_split: normalizedConfig.valSplit,
          test_split: normalizedConfig.testSplit,
        };
        if (normalizedConfig.enableEarlyStopping && normalizedConfig.patience) {
          configPayload.patience = normalizedConfig.patience;
        }
        await saveDetectionTrainingConfigAPI(configPayload);
      } catch (configError) {
        console.warn("Failed to persist detection training config:", configError);
      }

      try {
        const response = await startDetectionTrainingAPI(payload);
        Swal.close();

        const { status, message, job } = response?.data || {};
        if (status === "success") {
          const jobId = job?.job_id ? `\nJob ID: ${job.job_id}` : "";
          await Swal.fire({
            title: "Training Started",
            text: `${message || "Training has started in the background."}${jobId}`,
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
            timerProgressBar: true,
          });
          navigate("/ModelManagement", { state: { projectId: modelInfo?.projectId || modelInfo?.project_id } });
          return true;
        } else {
          Swal.fire("Training Failed", message || "Unable to start training.", "error");
          return false;
        }
      } catch (error) {
        Swal.close();
        const errorMessage = error?.response?.data?.message || error.message || "An error occurred while starting training.";
        Swal.fire("Error", errorMessage, "error");
        return false;
      }
    } catch (error) {
      Swal.fire("Error", error.message || "Unable to initiate training.", "error");
      return false;
    }
  };

  const handlers = useMemo(() => ({
    handleUnstructuredUpload,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileInput,
    handleSaveDetectionData,
    handleGetOneDetectionData,
    handleDownloadDetectionData,
    handleStartDetectionTraining,
  }), [
    handleUnstructuredUpload,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileInput,
    handleSaveDetectionData,
    handleGetOneDetectionData,
    handleDownloadDetectionData,
    handleStartDetectionTraining,
  ]);


  return {
    classes,
    color,
    images,
    selectedImage,
    setSelectedImage,
    selectedImages,
    setSelectedImages,
    dragOver,
    setDragOver,
    activeDragClass,
    showDeleteButton,
    setShowDeleteButton,
    handlers
  };
};



export default useDetectionLabelHook;
